import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useKeyVaultCalc } from '../../hooks/useCalculate'
import { useAnalytics } from '../../hooks/useAnalytics'
import type { KeyVaultRequest } from '../../api/types'
import { TrustBar } from '../shared/TrustBar'
import { ShareButton } from '../shared/ShareButton'
import { FieldTooltip } from '../shared/FieldTooltip'
import { ScenarioPresets } from '../shared/ScenarioPresets'

const TIER_OPTIONS = [
  {
    value: 'standard' as const,
    label: 'Standard',
    description: 'Software-protected keys. Per-operation billing with 10,000 ops/month free.',
  },
  {
    value: 'premium' as const,
    label: 'Premium',
    description: 'HSM-backed key storage available. Software key ops same rate as Standard.',
  },
  {
    value: 'managed_hsm' as const,
    label: 'Managed HSM',
    description: 'Dedicated HSM hardware. Billed by the hour regardless of operations.',
    warning: true,
  },
]

function fmt(n: number): string {
  return n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 4 })
}

function fmtOps(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toLocaleString('en-GB', { maximumFractionDigits: 1 })}M`
  if (n >= 1_000) return `${(n / 1_000).toLocaleString('en-GB', { maximumFractionDigits: 1 })}K`
  return n.toLocaleString('en-GB')
}

export default function KeyVaultCalc() {
  const [inputs, setInputs] = useState<KeyVaultRequest>({
    tier: 'standard',
    softwareOpsPerMonth: 10000,
    hsmOpsPerMonth: 0,
    softwareKeyCount: 5,
    hsmKeyCount: 0,
    certificateRenewalsPerMonth: 0,
    managedHsmHoursPerMonth: 744,
    loggingEnabled: false,
  })

  const { data, isLoading, isError } = useKeyVaultCalc(inputs)
  const { trackCalculatorUsed } = useAnalytics()
  useEffect(() => {
    if (data?.totalMonthly) {
      trackCalculatorUsed({ calculator: 'key-vault', result_gbp: data.totalMonthly, tier: inputs.tier })
    }
  }, [data?.totalMonthly])

  function set<K extends keyof KeyVaultRequest>(key: K, value: KeyVaultRequest[K]) {
    setInputs((prev) => ({ ...prev, [key]: value }))
  }

  const isManagedHsm = inputs.tier === 'managed_hsm'

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6">
      {/* ── Inputs ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-5">

        <ScenarioPresets presets={[
          {
            label: 'Dev secrets store',
            description: 'Standard · 10,000 ops/mo · 2 certs — low-cost app secrets vault',
            apply: () => { set('tier', 'standard'); set('softwareOpsPerMonth', 10000); set('hsmOpsPerMonth', 0); set('managedHsmHoursPerMonth', 0); },
          },
          {
            label: 'Production app (Premium)',
            description: 'Premium · 500K software + 50K HSM ops/mo · 10 certs',
            apply: () => { set('tier', 'premium'); set('softwareOpsPerMonth', 500000); set('hsmOpsPerMonth', 50000); set('managedHsmHoursPerMonth', 0); },
          },
          {
            label: 'Managed HSM',
            description: 'Dedicated HSM · always-on · 200K ops/mo — regulated industries',
            apply: () => { set('tier', 'managed_hsm'); set('managedHsmHoursPerMonth', 730); set('softwareOpsPerMonth', 0); set('hsmOpsPerMonth', 200000); },
          },
        ]} />

        {/* Tier selector */}
        <section
          className="rounded-lg p-5"
          style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
        >
          <h2 className="text-[13px] font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-tertiary)' }}>
            Key Vault Tier
          </h2>
          <div className="flex flex-col gap-2">
            {TIER_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => set('tier', opt.value)}
                className="flex items-start gap-3 px-4 py-3 rounded-lg text-left transition-colors"
                style={{
                  border: inputs.tier === opt.value
                    ? '1.5px solid #0078d4'
                    : '1px solid var(--outline-variant)',
                  backgroundColor: inputs.tier === opt.value
                    ? 'rgba(0,120,212,0.06)'
                    : 'var(--surface-container-low)',
                }}
              >
                <span
                  className="material-symbols-outlined text-[20px] mt-0.5 shrink-0"
                  style={{ color: opt.warning ? '#e8b000' : '#0078d4' }}
                >
                  {opt.warning ? 'warning' : 'lock'}
                </span>
                <div>
                  <p className="text-[13px] font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {opt.label}
                  </p>
                  <p className="text-[12px] mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                    {opt.description}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </section>

        {/* Managed HSM warning */}
        {isManagedHsm && (
          <div
            className="rounded-lg px-4 py-3 text-[12px] leading-relaxed"
            style={{ backgroundColor: 'rgba(232,176,0,0.08)', border: '1px solid rgba(232,176,0,0.4)', color: 'var(--text-secondary)' }}
          >
            <strong style={{ color: '#e8b000' }}>⚠ Managed HSM is a dedicated service.</strong> Unlike
            standard Key Vault, Managed HSM runs on dedicated hardware and is billed by the hour
            regardless of how many operations you perform. At UK South rates this is approximately
            £2,700/month continuously. This is appropriate for regulated workloads
            (PCI-DSS, FIPS 140-2 Level 3) — not for typical application secret management.
          </div>
        )}

        {/* Managed HSM hours */}
        {isManagedHsm && (
          <section
            className="rounded-lg p-5"
            style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
          >
            <h2 className="text-[13px] font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-tertiary)' }}>
              Managed HSM Hours
            </h2>
            <div>
              <label className="block text-[12px] mb-1" style={{ color: 'var(--text-secondary)' }}>
                Hours active per month (max 744)
              </label>
              <input
                type="number"
                min={0}
                max={744}
                value={inputs.managedHsmHoursPerMonth}
                onChange={(e) => set('managedHsmHoursPerMonth', Number(e.target.value))}
                className="w-full px-3 py-2 rounded text-[13px]"
                style={{
                  backgroundColor: 'var(--surface-container-low)',
                  border: '1px solid var(--outline-variant)',
                  color: 'var(--text-primary)',
                }}
              />
              <p className="text-[11px] mt-1" style={{ color: 'var(--text-tertiary)' }}>
                744 hours = full month (Managed HSM cannot be paused)
              </p>
            </div>
          </section>
        )}

        {/* Operations */}
        {!isManagedHsm && (
          <section
            className="rounded-lg p-5"
            style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
          >
            <h2 className="text-[13px] font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-tertiary)' }}>
              Operations per Month
            </h2>

            {/* Guidance callout */}
            <div
              className="rounded px-3 py-2 mb-4 text-[12px] leading-relaxed"
              style={{ backgroundColor: 'rgba(0,120,212,0.06)', border: '1px solid rgba(0,120,212,0.2)', color: 'var(--text-secondary)' }}
            >
              <strong style={{ color: '#0078d4' }}>Estimating your operations count:</strong>
              <ul className="mt-1 ml-3 list-disc space-y-0.5">
                <li>Typical web app (read secret on startup): ~1,000–5,000 ops/month</li>
                <li>App reading secrets on every request (100 req/s): ~260M ops/month</li>
                <li>CI/CD pipeline (100 deploys/month, 10 secrets each): ~1,000 ops/month</li>
              </ul>
              <p className="mt-1">Most applications stay within the 10,000/month free tier.</p>
            </div>

            <div className="flex flex-col gap-3">
              <div>
                <label className="flex items-center text-[12px] mb-1" style={{ color: 'var(--text-secondary)' }}>
                  Software-protected key operations/month
                  <span className="ml-1 text-[11px]" style={{ color: 'var(--text-tertiary)' }}>(first 10,000 free)</span>
                  <FieldTooltip content="Includes Get, Set, Delete, List on secrets, keys, and certificates. Each API call = 1 operation. Secrets cached in-process don't generate ops on every request." />
                </label>
                <input
                  type="number"
                  min={0}
                  value={inputs.softwareOpsPerMonth}
                  onChange={(e) => set('softwareOpsPerMonth', Number(e.target.value))}
                  className="w-full px-3 py-2 rounded text-[13px]"
                  style={{
                    backgroundColor: 'var(--surface-container-low)',
                    border: '1px solid var(--outline-variant)',
                    color: 'var(--text-primary)',
                  }}
                />
              </div>

              {inputs.tier === 'premium' && (
                <div>
                  <label className="flex items-center text-[12px] mb-1" style={{ color: 'var(--text-secondary)' }}>
                    HSM-protected key operations/month
                    <FieldTooltip content="HSM-backed operations use dedicated hardware and are billed at a higher per-operation rate than software keys. Typical use: signing or unwrapping with RSA keys stored in HSM." />
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={inputs.hsmOpsPerMonth}
                    onChange={(e) => set('hsmOpsPerMonth', Number(e.target.value))}
                    className="w-full px-3 py-2 rounded text-[13px]"
                    style={{
                      backgroundColor: 'var(--surface-container-low)',
                      border: '1px solid var(--outline-variant)',
                      color: 'var(--text-primary)',
                    }}
                  />
                </div>
              )}
            </div>
          </section>
        )}

        {/* Key storage (Premium only) */}
        {inputs.tier === 'premium' && (
          <section
            className="rounded-lg p-5"
            style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
          >
            <h2 className="text-[13px] font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-tertiary)' }}>
              Key Storage
            </h2>
            <div>
              <label className="block text-[12px] mb-1" style={{ color: 'var(--text-secondary)' }}>
                HSM-protected keys (RSA 2048-bit)
                <span className="ml-1 text-[11px]" style={{ color: 'var(--text-tertiary)' }}>£0.755/key/month</span>
              </label>
              <input
                type="number"
                min={0}
                value={inputs.hsmKeyCount}
                onChange={(e) => set('hsmKeyCount', Number(e.target.value))}
                className="w-full px-3 py-2 rounded text-[13px]"
                style={{
                  backgroundColor: 'var(--surface-container-low)',
                  border: '1px solid var(--outline-variant)',
                  color: 'var(--text-primary)',
                }}
              />
            </div>
          </section>
        )}

        {/* Certificates */}
        {!isManagedHsm && (
          <section
            className="rounded-lg p-5"
            style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
          >
            <h2 className="text-[13px] font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-tertiary)' }}>
              Certificates
            </h2>
            <div>
              <label className="block text-[12px] mb-1" style={{ color: 'var(--text-secondary)' }}>
                Certificate renewals per month
                <span className="ml-1 text-[11px]" style={{ color: 'var(--text-tertiary)' }}>£2.26/renewal</span>
              </label>
              <input
                type="number"
                min={0}
                value={inputs.certificateRenewalsPerMonth}
                onChange={(e) => set('certificateRenewalsPerMonth', Number(e.target.value))}
                className="w-full px-3 py-2 rounded text-[13px]"
                style={{
                  backgroundColor: 'var(--surface-container-low)',
                  border: '1px solid var(--outline-variant)',
                  color: 'var(--text-primary)',
                }}
              />
            </div>
          </section>
        )}

        {/* Logging */}
        {!isManagedHsm && (
          <section
            className="rounded-lg p-5"
            style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
          >
            <h2 className="text-[13px] font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-tertiary)' }}>
              Diagnostic Logging
            </h2>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={inputs.loggingEnabled}
                onChange={(e) => set('loggingEnabled', e.target.checked)}
                className="w-4 h-4 rounded"
                style={{ accentColor: '#0078d4' }}
              />
              <span className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>
                Send Key Vault diagnostic logs to Log Analytics
              </span>
            </label>
          </section>
        )}
      </div>

      {/* ── Results ────────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-4">
        <section
          className="rounded-lg p-5"
          style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[13px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
              Monthly Estimate (PAYG)
            </h2>
            <TrustBar />
          </div>

          {isLoading && (
            <p className="text-[13px]" style={{ color: 'var(--text-tertiary)' }}>Calculating…</p>
          )}
          {isError && (
            <p className="text-[13px]" style={{ color: '#a4262c' }}>Error fetching estimate. Please try again.</p>
          )}

          {data && (
            <>
              {/* Total */}
              <div
                className="rounded-lg px-4 py-4 mb-4"
                style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid var(--outline-variant)' }}
              >
                <p className="text-[11px] uppercase tracking-wider mb-1" style={{ color: 'var(--text-tertiary)' }}>
                  Estimated monthly cost
                </p>
                <p className="text-[28px] font-bold font-mono" style={{ color: '#0078d4' }}>
                  £{fmt(data.totalMonthly)}
                </p>
                <p className="text-[11px] mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                  UK South · GBP · estimate only
                </p>
              </div>

              {/* Managed HSM breakdown */}
              {data.managedHsmWarning && (
                <div className="flex flex-col gap-2 mb-4">
                  <div className="flex justify-between items-center py-2" style={{ borderBottom: '1px solid var(--outline-variant)' }}>
                    <span className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>
                      Managed HSM ({inputs.managedHsmHoursPerMonth}h × £{fmt(data.managedHsmRate ?? 0)}/hr)
                    </span>
                    <span className="text-[13px] font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>
                      £{fmt(data.breakdown.managedHsm)}
                    </span>
                  </div>
                </div>
              )}

              {/* Standard/Premium breakdown */}
              {!data.managedHsmWarning && (
                <div className="flex flex-col gap-1 mb-4 text-[13px]">
                  {/* Free tier */}
                  {data.breakdown.operations.freeOpsApplied > 0 && (
                    <div className="flex justify-between items-center py-1.5" style={{ borderBottom: '1px solid var(--outline-variant)' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>
                        Free operations ({fmtOps(data.breakdown.operations.freeOpsApplied)} ops)
                      </span>
                      <span className="font-mono" style={{ color: '#107c10' }}>£0.00</span>
                    </div>
                  )}

                  {/* Billable ops */}
                  {data.breakdown.operations.billableOps > 0 && (
                    <div className="flex justify-between items-center py-1.5" style={{ borderBottom: '1px solid var(--outline-variant)' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>
                        Key operations ({fmtOps(data.breakdown.operations.billableOps)} billable ops
                        × £{data.breakdown.operations.ratePerTenK}/10K)
                      </span>
                      <span className="font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>
                        £{fmt(data.breakdown.operations.softwareCost)}
                      </span>
                    </div>
                  )}

                  {/* HSM ops (Premium) */}
                  {data.breakdown.operations.hsmCost > 0 && (
                    <div className="flex justify-between items-center py-1.5" style={{ borderBottom: '1px solid var(--outline-variant)' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>
                        HSM key operations ({fmtOps(inputs.hsmOpsPerMonth ?? 0)} ops
                        × £{data.breakdown.operations.hsmRatePerTenK}/10K)
                      </span>
                      <span className="font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>
                        £{fmt(data.breakdown.operations.hsmCost)}
                      </span>
                    </div>
                  )}

                  {/* HSM key storage (Premium) */}
                  {data.breakdown.keyStorage.hsmCost > 0 && (
                    <div className="flex justify-between items-center py-1.5" style={{ borderBottom: '1px solid var(--outline-variant)' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>
                        HSM key storage ({inputs.hsmKeyCount} keys × £{data.breakdown.keyStorage.hsmRatePerKey}/key/month)
                      </span>
                      <span className="font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>
                        £{fmt(data.breakdown.keyStorage.hsmCost)}
                      </span>
                    </div>
                  )}

                  {/* Certificates */}
                  {data.breakdown.certificates > 0 && (
                    <div className="flex justify-between items-center py-1.5" style={{ borderBottom: '1px solid var(--outline-variant)' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>
                        Certificate renewals ({inputs.certificateRenewalsPerMonth} × £{data.breakdown.certRateEach}/renewal)
                      </span>
                      <span className="font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>
                        £{fmt(data.breakdown.certificates)}
                      </span>
                    </div>
                  )}

                  {/* Zero cost case */}
                  {data.totalMonthly === 0 && (
                    <div
                      className="rounded px-3 py-2 text-[12px] mt-1"
                      style={{ backgroundColor: 'rgba(16,124,16,0.06)', border: '1px solid rgba(16,124,16,0.2)', color: '#107c10' }}
                    >
                      All operations within the 10,000/month free tier. Cost is £0.00.
                    </div>
                  )}
                </div>
              )}

              {/* Formula disclosure */}
              {!data.managedHsmWarning && data.breakdown.operations.billableOps > 0 && (
                <details className="text-[12px] mb-3">
                  <summary className="cursor-pointer" style={{ color: 'var(--text-tertiary)' }}>
                    Show formula
                  </summary>
                  <div className="mt-2 px-3 py-2 rounded font-mono" style={{ backgroundColor: 'var(--surface-container-low)', color: 'var(--text-secondary)' }}>
                    <p>Billable ops = {fmtOps(inputs.softwareOpsPerMonth ?? 0)} − 10,000 (free) = {fmtOps(data.breakdown.operations.billableOps)}</p>
                    <p>Ops cost = ({data.breakdown.operations.billableOps} ÷ 10,000) × £{data.breakdown.operations.ratePerTenK} = £{fmt(data.breakdown.operations.softwareCost)}</p>
                    {data.breakdown.certificates > 0 && (
                      <p>Cert cost = {inputs.certificateRenewalsPerMonth} × £{data.breakdown.certRateEach} = £{fmt(data.breakdown.certificates)}</p>
                    )}
                    <p className="mt-1 font-semibold">Total = £{fmt(data.totalMonthly)}</p>
                  </div>
                </details>
              )}

              {/* Logging cross-link */}
              {data.logAnalyticsCrossLinkURL && (
                <div
                  className="rounded px-3 py-2 text-[12px] mt-2"
                  style={{ backgroundColor: 'rgba(0,120,212,0.05)', border: '1px solid rgba(0,120,212,0.2)', color: 'var(--text-secondary)' }}
                >
                  Key Vault diagnostic logs typically generate 0.5–2 GB/month for moderate usage.{' '}
                  <Link to="/log-analytics" className="text-[#0078d4] hover:underline">
                    Estimate your Log Analytics cost →
                  </Link>
                </div>
              )}

              {/* Pricing note */}
              <p className="text-[11px] mt-3 italic" style={{ color: 'var(--text-tertiary)' }}>
                {data.pricingNote}
              </p>
            </>
          )}
        </section>

        <ShareButton inputs={inputs} result={data ?? null} service="key-vault" />

        <a href="https://azure.microsoft.com/en-gb/pricing/details/key-vault/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[10px] font-mono mt-2" style={{ color: 'var(--text-tertiary)' }}>
          <span className="material-symbols-outlined text-[13px]">open_in_new</span>
          Verify on official Azure pricing page
        </a>
      </div>
    </div>
  )
}
